import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
  Table,
  Input,
  Label,
  Button
} from 'reactstrap'
import BreadCrumb from 'Components/Common/BreadCrumb'
import Pagination from 'Components/Common/Pagination'
import { fetchAllPhotoBooths, fetchSalesRecords } from 'helpers/backend_helper'
import { toast } from 'react-toastify'
import { PERPAGE_DEFAULT, PERPAGE_OPTIONS } from 'lib/constant'
import ReactApexChart from 'react-apexcharts'
import {
  boothChartLayout,
  CHART_MAX_HEIGHT,
  CHART_AXIS_PADDING
} from './boothChartLayout'

// NOT: `resolveMediaUrl` 2026-08-17'de kaldırıldı. Tek kullanıcısı satış
// tablosundaki arka plan küçük görselleriydi; o hücre artık görsel yerine
// arka plan ADINI yazıyor (ortalama 399 KB'lik JPEG'i 34 px kutuda göstermek
// 100 satırlık sayfada ~80 MB indirme demekti). Küçük görsel geri gelirse
// önce gerçek bir thumbnail üretimi gerekir; bu yardımcı da onunla döner.

interface IOrderSummary {
  orderCode?: string
  totalAmount?: number
  status?: string
  createdAt?: string
  photoCount?: number
  categoryId?: {
    name?: any
  }
  promoCode?: string
  promoDiscountType?: number // 1=Percentage, 2=Amount
  promoDiscountValue?: number
  activeActionId?: {
    name?: string
    label?: string
  }
  selectedBackgrounds?: string[] // Çekimde seçilen arka plan URL'leri (thumbnail)
  // Backend'in URL'lerden çözdüğü okunabilir adlar: "85 (ŞEHİR GÖRSELLERİ)".
  // Sipariş yalnız URL sakladığı için ad ancak Background.imageLink eşleşmesiyle
  // bulunabiliyor; bu işi backend tek sorguda yapıyor (helpers/backgroundNames.js).
  selectedBackgroundNames?: string[]
}

interface IPhotoBoothInfo {
  _id: string
  name?: string
  serialNumber?: string
  location?: {
    city?: string
    address?: string
  }
}

interface ISalesSummary {
  count: number
  totalAmount: number
  cashAmount: number
  cardAmount: number
  cashCount: number
  cardCount: number
}

interface IBoothSale {
  name: string
  amount: number
  count: number
}

interface IProductInfo {
  _id: string
  name?: string
  price?: number
}

export interface ISaleRecord {
  _id: string
  orderId?: IOrderSummary
  photoBoothId?: IPhotoBoothInfo
  products?: IProductInfo[]
  price?: number
  totalAmount?: number
  paymentType?: string
  status?: string
  currency?: string
  createdAt?: string
}

const statusColorMap: Record<string, string> = {
  paid: 'success',
  failed: 'danger',
  cancelled: 'secondary',
  pending: 'warning',
  payment_pending: 'warning',
  payment_processing: 'info'
}

const formatCurrency = (value: number, currency?: string) => {
  const code = currency && typeof currency === 'string' ? currency.toUpperCase() : 'TRY'
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2
    }).format(value)
  } catch {
    return value.toLocaleString(undefined, { minimumFractionDigits: 2 }) + ` ${code}`
  }
}

const getCategoryName = (categoryId: any): string => {
  if (!categoryId) return '-'
  if (typeof categoryId.name === 'string') return categoryId.name
  if (Array.isArray(categoryId.name) && categoryId.name.length > 0) {
    return categoryId.name[0]?.text || '-'
  }
  return '-'
}

const Sales = () => {
  document.title = 'Sales | SocialBooth - Admin Dashboard'

  const [sales, setSales] = useState<ISaleRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  // Sayfa başına kayıt — kullanıcı seçer (10/50/100). Backend `limit`i zaten destekliyor.
  const [perPage, setPerPage] = useState<number>(PERPAGE_DEFAULT)
  const [summary, setSummary] = useState<ISalesSummary>({
    count: 0, totalAmount: 0, cashAmount: 0, cardAmount: 0, cashCount: 0, cardCount: 0
  })
  const [boothSales, setBoothSales] = useState<IBoothSale[]>([])
  const [showAllBooths, setShowAllBooths] = useState(false)

  // Filter states
  const [paymentTypeFilter, setPaymentTypeFilter] = useState<string>('all')
  const [dateFilter, setDateFilter] = useState<string>('week')
  const [customStartDate, setCustomStartDate] = useState<string>('')
  const [customEndDate, setCustomEndDate] = useState<string>('')
  // Kabin filtresi (müşteri talebi 2026-08-22). Backend `boothId` parametresini
  // ZATEN destekliyordu (routers/payments.js → query.photoBoothId); eksik olan
  // yalnız arayüzdü. 'all' = filtre yok.
  const [boothFilter, setBoothFilter] = useState<string>('all')
  const [boothOptions, setBoothOptions] = useState<{ _id: string; name: string }[]>([])

  // Kabin listesi (açılır liste için) — SAYFASIZ uçtan. Bkz. fetchAllPhotoBooths:
  // sayfalı uç 10 kayıtla dönüyor, 11. kabin sessizce kaybolurdu.
  useEffect(() => {
    let cancelled = false
    const loadBooths = async () => {
      try {
        // 🔑 `helpers/api_helper.ts` response interceptor'ı `response.data`yı
        //    döndürür → burada backend GÖVDESİ elimizdedir:
        //    { success: true, data: [ {_id, name, ...} ] }
        //    Üç olası şekli de açıkça karşılıyoruz ki interceptor değişirse
        //    liste sessizce BOŞ kalmasın (boş liste = filtre yokmuş gibi görünür).
        const raw: any = await fetchAllPhotoBooths()
        const list: any[] = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.data?.data)
              ? raw.data.data
              : []
        if (cancelled) return
        if (list.length === 0) {
          console.warn('[Sales] Kabin listesi BOŞ döndü — filtre kullanılamaz.', raw)
        }
        setBoothOptions(
          list
            .filter((b) => b && b._id)
            .map((b) => ({ _id: String(b._id), name: b.name || '(isimsiz kabin)' }))
        )
      } catch (e) {
        // Kabin listesi YAN İŞTİR: gelmezse satış tablosu yine çalışmalı.
        console.error('[Sales] Kabin listesi yüklenemedi:', e)
      }
    }
    loadBooths()
    return () => {
      cancelled = true
    }
  }, [])

  const getDateRange = () => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    switch (dateFilter) {
      case 'today':
        return {
          startDate: today.toISOString(),
          endDate: new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString()
        }
      case 'week':
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
        return {
          startDate: weekAgo.toISOString(),
          endDate: new Date().toISOString()
        }
      case 'month':
        const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
        return {
          startDate: monthAgo.toISOString(),
          endDate: new Date().toISOString()
        }
      case 'custom':
        if (customStartDate && customEndDate) {
          return {
            startDate: new Date(customStartDate).toISOString(),
            endDate: new Date(customEndDate + ' 23:59:59').toISOString()
          }
        }
        return {}
      default:
        return {}
    }
  }

  const totals = useMemo(() => {
    const currency = sales[0]?.currency || 'TRY'

    return {
      count: summary.count,
      amount: summary.totalAmount,
      cashAmount: summary.cashAmount,
      cardAmount: summary.cardAmount,
      cashCount: summary.cashCount,
      cardCount: summary.cardCount,
      currency
    }
  }, [sales, summary])

  const loadSales = useCallback(
    async (page: number) => {
      setLoading(true)
      try {
        const dateRange = getDateRange()
        const params: any = { page, limit: perPage }

        if (paymentTypeFilter !== 'all') {
          params.paymentType = paymentTypeFilter
        }

        if (boothFilter !== 'all') {
          params.boothId = boothFilter
        }

        if (dateRange.startDate) params.startDate = dateRange.startDate
        if (dateRange.endDate) params.endDate = dateRange.endDate

        const raw: any = await fetchSalesRecords(params)
        // axios interceptor returns response.data, but check both cases
        const body = raw?.ok !== undefined ? raw : raw?.data ?? raw
        const rows: ISaleRecord[] = body.data ?? []
        setSales(rows)
        setTotalPages(body.totalPages ?? 1)
        if (body.summary) {
          setSummary(body.summary)
        }
        if (body.boothSales) {
          setBoothSales(body.boothSales)
          setShowAllBooths(false)
        }
      } catch (error) {
        toast.error(error as string)
      } finally {
        setLoading(false)
      }
    },
    [paymentTypeFilter, boothFilter, dateFilter, customStartDate, customEndDate, perPage]
  )

  useEffect(() => {
    loadSales(currentPage)
  }, [currentPage, paymentTypeFilter, boothFilter, dateFilter, customStartDate, customEndDate, perPage, loadSales])

  const formatDate = (value?: string) => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  /**
   * "Bu akışta arka plan SEÇİLMEZ" durumunun TEK kaynağı.
   *
   * 🔑 Boş hücre iki farklı şey demek olabilir: (a) akışta arka plan kavramı
   * yok, (b) seçildi ama kaydedilemedi (22 Ağu'ya kadarki kusur). İkisini aynı
   * "-" ile göstermek, düzeltmeden sonra da "hâlâ yazmıyor" şikâyetini davet
   * ederdi. Bu yüzden sebep AÇIKÇA yazılır.
   *
   * Etiket hem TABLO hücresinde hem EXCEL sütununda kullanılır; iki kopya
   * bırakmak, birine yeni akış eklenip diğerine eklenmemesi demekti
   * ("kaç dosya değil KAÇ KATMAN" dersi). Tek yer burasıdır.
   */
  const noBackgroundReason = (
    sale: ISaleRecord
  ): { label: string; title: string } | null => {
    const flow = sale.orderId?.activeActionId?.name || sale.orderId?.categoryId?.name || ''
    if (flow === 'photo_roll') {
      return {
        label: 'Sabit fon (Photo Roll)',
        title:
          'Photo Roll akışında arka plan seçilmez: sabit beyaz fon + sabit çerçeve kullanılır.'
      }
    }
    if (flow === 'cepten_baski') {
      return {
        label: 'Yok (cepten baskı)',
        title:
          'Cepten baskıda arka plan kavramı yoktur; müşterinin kendi fotoğrafı basılır.'
      }
    }
    return null
  }

  /**
   * Excel'deki "Arka Plan" hücresi. Panel tablosundaki hücreyle AYNI mantık ve
   * AYNI etiketler (bkz. noBackgroundReason).
   */
  const backgroundCell = (sale: ISaleRecord): string => {
    const names = sale.orderId?.selectedBackgroundNames || []
    if (names.length > 0) return names.join(', ')
    const urls = sale.orderId?.selectedBackgrounds || []
    if (urls.length > 0) return `${urls.length} arka plan (adı yok)`
    return noBackgroundReason(sale)?.label || ''
  }

  // Aktif filtrelerle TÜM kayıtları çekip gerçek .xlsx olarak indirir.
  // CSV yerine xlsx: Türkçe Excel'de ayraç/karakter sorunu yaşanmaz.
  const handleExportExcel = async () => {
    setExporting(true)
    try {
      const dateRange = getDateRange()
      // Tek istekte tüm filtreli kayıtlar (backend limit'i destekliyor)
      const params: any = { page: 1, limit: 50000 }
      if (paymentTypeFilter !== 'all') params.paymentType = paymentTypeFilter
      // Excel de EKRANDAKİ filtreyle aynı kümeyi vermeli; kabin filtresi
      // burada unutulursa kullanıcı "tek kabin" görüp "tüm kabinler" indirir.
      if (boothFilter !== 'all') params.boothId = boothFilter
      if (dateRange.startDate) params.startDate = dateRange.startDate
      if (dateRange.endDate) params.endDate = dateRange.endDate

      const raw: any = await fetchSalesRecords(params)
      const body = raw?.ok !== undefined ? raw : raw?.data ?? raw
      const rows: ISaleRecord[] = body.data ?? []

      if (rows.length === 0) {
        toast.info('Dışa aktarılacak kayıt bulunamadı')
        return
      }

      const excelRows = rows.map((sale, index) => {
        const amount = sale.totalAmount ?? sale.price ?? 0
        const loc = sale.photoBoothId?.location
        const categoryName =
          getCategoryName(sale.orderId?.categoryId) !== '-'
            ? getCategoryName(sale.orderId?.categoryId)
            : sale.orderId?.activeActionId?.name ||
              sale.orderId?.activeActionId?.label ||
              '-'
        const paymentTypeLabel =
          sale.paymentType === 'creditCard'
            ? 'Kredi Kartı'
            : sale.paymentType === 'cash'
              ? 'Nakit'
              : sale.paymentType === 'promotionCode'
                ? 'Promosyon Kodu'
                : sale.paymentType || '-'

        return {
          '#': index + 1,
          'Tarih': formatDate(sale.createdAt || sale.orderId?.createdAt),
          'Kabin': sale.photoBoothId?.name || '-',
          'Lokasyon': loc
            ? [loc.city, loc.address].filter(Boolean).join(', ') || '-'
            : '-',
          'Hizmet': categoryName,
          // 📄 2026-08-17 müşteri talebi: kullanılan arka plan adı.
          // Birden fazla arka plan seçilebildiği için virgülle birleştirilir.
          // 🔑 2026-08-22: boş hücre bırakmak yerine SEBEP yazılır. Arka plan
          // yalnızca "büyülü selfie" akışında seçilir; photoroll sabit beyaz
          // fon + sabit çerçeve kullanır, cepten baskıda kavram yoktur. Boş
          // hücre "seçilmedi" ile "kaydedilemedi"yi aynı gösterirdi.
          'Arka Plan': backgroundCell(sale),
          'Fotoğraf Adedi': sale.orderId?.photoCount || sale.products?.length || 0,
          'Tutar': amount,
          'Para Birimi': (sale.currency || 'TRY').toUpperCase(),
          'Ödeme Tipi': paymentTypeLabel,
          'Promosyon Kodu': sale.orderId?.promoCode || '',
          'İndirim': sale.orderId?.promoDiscountValue
            ? sale.orderId?.promoDiscountType === 1
              ? `%${sale.orderId.promoDiscountValue}`
              : `${sale.orderId.promoDiscountValue} TL`
            : '',
          'Durum': sale.status || sale.orderId?.status || '-',
          'Sipariş Kodu': sale.orderId?.orderCode || ''
        }
      })

      // xlsx'i dinamik import et — ana bundle'ı şişirmesin
      const XLSX = await import('xlsx')
      const ws = XLSX.utils.json_to_sheet(excelRows)
      ws['!cols'] = [
        { wch: 5 },  // #
        { wch: 17 }, // Tarih
        { wch: 20 }, // Kabin
        { wch: 30 }, // Lokasyon
        { wch: 18 }, // Hizmet
        { wch: 26 }, // Arka Plan
        { wch: 14 }, // Fotoğraf Adedi
        { wch: 10 }, // Tutar
        { wch: 11 }, // Para Birimi
        { wch: 15 }, // Ödeme Tipi
        { wch: 15 }, // Promosyon Kodu
        { wch: 10 }, // İndirim
        { wch: 10 }, // Durum
        { wch: 14 }  // Sipariş Kodu
      ]
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Satışlar')
      const stamp = new Date().toISOString().slice(0, 10)
      XLSX.writeFile(wb, `socialbooth-satislar-${stamp}.xlsx`)
      toast.success(`${rows.length} kayıt Excel'e aktarıldı`)
    } catch (error) {
      console.error('[Sales] Excel export failed:', error)
      toast.error('Excel dışa aktarma başarısız oldu')
    } finally {
      setExporting(false)
    }
  }

  const renderStatus = (status?: string) => {
    if (!status) return '-'
    const normalized = status.toLowerCase()
    const color = statusColorMap[normalized] || 'primary'
    return (
      <Badge color={color} className='text-uppercase'>
        {status.replace(/_/g, ' ')}
      </Badge>
    )
  }

  // Chart data for payment type breakdown
  const pieChartOptions = {
    chart: {
      type: 'pie' as const,
      height: 250,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      }
    },
    labels: ['Cash', 'Credit Card'],
    colors: ['#0ab39c', '#405189'],
    legend: {
      position: 'bottom' as const
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val.toFixed(1)}%`,
      dropShadow: { enabled: false },
      offset: 0
    },
    plotOptions: {
      pie: {
        dataLabels: {
          offset: -5,
          minAngleToShowLabel: 10
        }
      }
    }
  }

  const pieChartSeries = [totals.cashAmount, totals.cardAmount]

  // Top booth chart (backend'den gelen aggregated data)
  // ─────────────────────────────────────────────────────────────────────
  // 📈 2026-08-17 müşteri: "Kabin sayısı artışı olduğunda lokasyon
  // isimlerinin yazdığı grafiği sayı artışına uygun yerleştirmeniz mümkün
  // mü? Belki daha inceltilebilir. Tahmini kabin sayısı 50 adet gibi olur."
  //
  // 50 kabinle ÖLÇÜLDÜ (gerçek ApexCharts, headless Chrome):
  //   • satır yüksekliği sabit 40px → grafik 2015px, pencere 400px
  //     → aynı anda grafiğin yalnızca **%19,9**'u görünüyordu (50'de 10 kabin)
  //   • "Others (45)" çubuğu 45 kabinin TOPLAMI olduğu için en büyük gerçek
  //     çubuğun **5,6 KATI** oluyor ve bütün gerçek çubukları sola eziyordu
  // ─────────────────────────────────────────────────────────────────────

  const { topN: TOP_N, restCount, showOthers, rowHeight, dense: denseChart } =
    boothChartLayout(boothSales.length, showAllBooths)
  const hasMoreBooths = boothSales.length > TOP_N

  const chartBooths = useMemo(() => {
    if (showAllBooths) return boothSales
    if (boothSales.length <= TOP_N) return boothSales

    const top = boothSales.slice(0, TOP_N)
    if (!showOthers) return top

    const rest = boothSales.slice(TOP_N)
    const othersAmount = rest.reduce((s, b) => s + b.amount, 0)
    const othersCount = rest.reduce((s, b) => s + b.count, 0)
    return [...top, { name: `Others (${rest.length})`, amount: othersAmount, count: othersCount }]
  }, [boothSales, showAllBooths, TOP_N, showOthers])

  const chartHeight = Math.max(250, chartBooths.length * rowHeight + CHART_AXIS_PADDING)
  const needsScroll = chartHeight > CHART_MAX_HEIGHT

  const barChartOptions = {
    chart: {
      type: 'bar' as const,
      height: chartHeight,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      },
      toolbar: {
        show: false
      }
    },
    plotOptions: {
      bar: {
        horizontal: true,
        // Yoğun modda ince çubuk + küçük yarıçap: 22px'lik satırda 4px yarıçap
        // çubuğu kapsül gibi gösterip uzunluk algısını bozuyor.
        borderRadius: denseChart ? 2 : 4,
        barHeight: denseChart ? '78%' : '70%',
        dataLabels: {
          position: 'top'
        }
      }
    },
    colors: ['#299cdb'],
    dataLabels: {
      // 🔑 Yoğun modda etiketler KAPATILMAMALI: grafik kaydırma penceresinden
      // uzun olduğu için x-ekseni (para ölçeği) en altta kalıyor ve neredeyse
      // hiç görünmüyor. Tutar yalnızca çubuğun ucunda okunabiliyor.
      enabled: true,
      formatter: (val: number) => formatCurrency(val, totals.currency),
      offsetX: denseChart ? 26 : 30,
      style: {
        fontSize: denseChart ? '10px' : '12px',
        fontWeight: denseChart ? 600 : 400,
        colors: ['#304758']
      }
    },
    xaxis: {
      categories: chartBooths.map((b) => b.name),
      labels: {
        formatter: (val: string) => formatCurrency(parseFloat(val), totals.currency),
        style: { fontSize: denseChart ? '11px' : '12px' }
      }
    },
    yaxis: {
      labels: {
        style: { fontSize: denseChart ? '11px' : '12px' },
        // Uzun lokasyon adları çizim alanını yemesin.
        maxWidth: 160
      },
      // Yoğun modda eksen başlığı yatay yer yiyor ve zaten gereksiz:
      // satırların lokasyon adı olduğu apaçık.
      title: denseChart ? undefined : { text: 'Photo Booth' }
    },
    grid: { padding: { left: 4, right: 12 } },
    tooltip: {
      y: { formatter: (val: number) => formatCurrency(val, totals.currency) }
    }
  }

  const barChartSeries = [
    {
      name: 'Sales',
      data: chartBooths.map((b) => b.amount)
    }
  ]

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb title='Sales' pageTitle='Sales' />

          {/* Summary Cards */}
          <Row className='mb-4'>
            <Col lg={3}>
              <Card className='card-animate h-100'>
                <CardBody>
                  <div className='d-flex align-items-center'>
                    <div className='flex-grow-1'>
                      <p className='text-uppercase fw-medium text-muted mb-0'>Total Sales</p>
                    </div>
                    <div className='flex-shrink-0'>
                      <h5 className='text-success fs-14 mb-0'>
                        <i className='ri-arrow-right-up-line fs-13 align-middle'></i> {totals.count}
                      </h5>
                    </div>
                  </div>
                  <div className='d-flex align-items-center justify-content-between mt-4'>
                    <div>
                      <h4 className='fs-22 fw-semibold ff-secondary mb-2'>
                        {formatCurrency(totals.amount, totals.currency)}
                      </h4>
                      <span className='badge badge-soft-success'>Records: {totals.count}</span>
                    </div>
                    <div className='avatar-sm flex-shrink-0'>
                      <span className='avatar-title bg-success rounded fs-3'>
                        <i className='bx bx-lira text-white'></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col lg={3}>
              <Card className='card-animate h-100'>
                <CardBody>
                  <div className='d-flex align-items-center'>
                    <div className='flex-grow-1'>
                      <p className='text-uppercase fw-medium text-muted mb-0'>Cash Payments</p>
                    </div>
                    <div className='flex-shrink-0'>
                      <h5 className='text-info fs-14 mb-0'>
                        <i className='ri-money-dollar-circle-line fs-13 align-middle'></i>{' '}
                        {totals.cashCount}
                      </h5>
                    </div>
                  </div>
                  <div className='d-flex align-items-center justify-content-between mt-4'>
                    <div>
                      <h4 className='fs-22 fw-semibold ff-secondary mb-2'>
                        {formatCurrency(totals.cashAmount, totals.currency)}
                      </h4>
                      <span className='badge badge-soft-info'>
                        {totals.amount > 0
                          ? ((totals.cashAmount / totals.amount) * 100).toFixed(1)
                          : 0}
                        %
                      </span>
                    </div>
                    <div className='avatar-sm flex-shrink-0'>
                      <span className='avatar-title bg-info rounded fs-3'>
                        <i className='bx bx-wallet text-white'></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col lg={3}>
              <Card className='card-animate h-100'>
                <CardBody>
                  <div className='d-flex align-items-center'>
                    <div className='flex-grow-1'>
                      <p className='text-uppercase fw-medium text-muted mb-0'>Card Payments</p>
                    </div>
                    <div className='flex-shrink-0'>
                      <h5 className='text-primary fs-14 mb-0'>
                        <i className='ri-bank-card-line fs-13 align-middle'></i> {totals.cardCount}
                      </h5>
                    </div>
                  </div>
                  <div className='d-flex align-items-center justify-content-between mt-4'>
                    <div>
                      <h4 className='fs-22 fw-semibold ff-secondary mb-2'>
                        {formatCurrency(totals.cardAmount, totals.currency)}
                      </h4>
                      <span className='badge badge-soft-primary'>
                        {totals.amount > 0
                          ? ((totals.cardAmount / totals.amount) * 100).toFixed(1)
                          : 0}
                        %
                      </span>
                    </div>
                    <div className='avatar-sm flex-shrink-0'>
                      <span className='avatar-title bg-primary rounded fs-3'>
                        <i className='bx bx-credit-card text-white'></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col lg={3}>
              <Card className='card-animate h-100'>
                <CardBody>
                  <div className='d-flex align-items-center'>
                    <div className='flex-grow-1'>
                      <p className='text-uppercase fw-medium text-muted mb-0'>Total Sales Count</p>
                    </div>
                  </div>
                  <div className='d-flex align-items-center justify-content-between mt-4'>
                    <div>
                      <h4 className='fs-22 fw-semibold ff-secondary mb-2'>
                        {totals.count}
                      </h4>
                      <span className='badge badge-soft-warning'>
                        {totals.cashCount} Cash / {totals.cardCount} Card
                      </span>
                    </div>
                    <div className='avatar-sm flex-shrink-0'>
                      <span className='avatar-title bg-warning rounded fs-3'>
                        <i className='bx bx-receipt text-white'></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Charts Row */}
          {sales.length > 0 && (
            <Row className='mb-4'>
              <Col xl={6} className='d-flex'>
                <Card className='w-100'>
                  <CardHeader>
                    <h4 className='card-title mb-0'>Payment Type Breakdown</h4>
                  </CardHeader>
                  <CardBody className='d-flex align-items-center justify-content-center'>
                    <ReactApexChart
                      options={pieChartOptions}
                      series={pieChartSeries}
                      type='pie'
                      height={250}
                    />
                  </CardBody>
                </Card>
              </Col>

              <Col xl={6} className='d-flex'>
                <Card className='w-100'>
                  <CardHeader className='d-flex align-items-center justify-content-between'>
                    <h4 className='card-title mb-0'>Top Performing Booths</h4>
                    {hasMoreBooths && (
                      <Button
                        color='soft-primary'
                        size='sm'
                        onClick={() => setShowAllBooths(!showAllBooths)}
                      >
                        {showAllBooths ? (
                          <>
                            <i className='ri-collapse-diagonal-line me-1'></i>
                            Show Top {TOP_N}
                          </>
                        ) : (
                          <>
                            <i className='ri-expand-diagonal-line me-1'></i>
                            Show All ({boothSales.length})
                          </>
                        )}
                      </Button>
                    )}
                  </CardHeader>
                  <CardBody>
                    {chartBooths.length > 0 ? (
                      <>
                        {/* Ne kadarının gösterildiğini AÇIKÇA yaz. "Others" kutusu
                            kaldırıldığında (kalan çok kalabalıkken ölçeği ezdiği
                            için) kalan kabinlerin sessizce yok olmaması gerekir. */}
                        {!showAllBooths && !showOthers && restCount > 0 && (
                          <div className='text-muted small mb-2'>
                            {boothSales.length} kabinden ilk {TOP_N} tanesi gösteriliyor.
                            Tümü için “Show All” düğmesini kullanın.
                          </div>
                        )}
                        <div
                          style={
                            needsScroll
                              ? { maxHeight: `${CHART_MAX_HEIGHT}px`, overflowY: 'auto' }
                              : undefined
                          }
                        >
                          <ReactApexChart
                            // Yükseklik kabin sayısıyla değiştiği için anahtar da
                            // yüksekliği içerir; yoksa tarih filtresi kabin sayısını
                            // değiştirdiğinde ApexCharts eski yükseklikte kalıyor.
                            key={`${showAllBooths ? 'all' : 'top'}-${chartHeight}`}
                            options={barChartOptions}
                            series={barChartSeries}
                            type='bar'
                            height={chartHeight}
                          />
                        </div>
                        {needsScroll && (
                          <div className='text-muted small mt-1'>
                            <i className='ri-arrow-up-down-line me-1'></i>
                            {chartBooths.length} kabin listeleniyor — kaydırarak tümünü görün.
                          </div>
                        )}
                      </>
                    ) : (
                      <div className='text-center py-4 text-muted'>No booth data available</div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* Filters */}
          <Card className='mb-4'>
            <CardBody>
              <Row className='g-3'>
                <Col md={3}>
                  <Label>Date Range</Label>
                  <Input
                    type='select'
                    value={dateFilter}
                    onChange={(e) => {
                      setDateFilter(e.target.value)
                      setCurrentPage(1)
                    }}
                  >
                    <option value='all'>All Time</option>
                    <option value='today'>Today</option>
                    <option value='week'>Last 7 Days</option>
                    <option value='month'>Last 30 Days</option>
                    <option value='custom'>Custom Range</option>
                  </Input>
                </Col>

                {dateFilter === 'custom' && (
                  <>
                    <Col md={3}>
                      <Label>Start Date</Label>
                      <Input
                        type='date'
                        value={customStartDate}
                        onChange={(e) => {
                          setCustomStartDate(e.target.value)
                          setCurrentPage(1)
                        }}
                      />
                    </Col>
                    <Col md={3}>
                      <Label>End Date</Label>
                      <Input
                        type='date'
                        value={customEndDate}
                        onChange={(e) => {
                          setCustomEndDate(e.target.value)
                          setCurrentPage(1)
                        }}
                      />
                    </Col>
                  </>
                )}

                <Col md={3}>
                  <Label>Booth</Label>
                  <Input
                    type='select'
                    value={boothFilter}
                    onChange={(e) => {
                      setBoothFilter(e.target.value)
                      setCurrentPage(1)
                    }}
                  >
                    <option value='all'>All Booths</option>
                    {boothOptions.map((b) => (
                      <option key={b._id} value={b._id}>
                        {b.name}
                      </option>
                    ))}
                  </Input>
                </Col>

                <Col md={3}>
                  <Label>Payment Type</Label>
                  <Input
                    type='select'
                    value={paymentTypeFilter}
                    onChange={(e) => {
                      setPaymentTypeFilter(e.target.value)
                      setCurrentPage(1)
                    }}
                  >
                    <option value='all'>All Types</option>
                    <option value='cash'>Cash</option>
                    <option value='creditCard'>Credit Card</option>
                    <option value='promotionCode'>Promotion Code</option>
                  </Input>
                </Col>

                <Col md={2}>
                  <Label>Rows per page</Label>
                  <Input
                    type='select'
                    value={perPage}
                    onChange={(e) => {
                      setPerPage(Number(e.target.value))
                      setCurrentPage(1) // boyut değişince 6. sayfada asılı kalmayalım
                    }}
                  >
                    {PERPAGE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </Input>
                </Col>

                <Col md={2} className='d-flex align-items-end'>
                  <Button
                    color='secondary'
                    outline
                    onClick={() => {
                      setDateFilter('all')
                      setPaymentTypeFilter('all')
                      setBoothFilter('all')
                      setCustomStartDate('')
                      setCustomEndDate('')
                      setCurrentPage(1)
                    }}
                    className='w-100'
                  >
                    <i className='ri-refresh-line align-middle'></i> Reset Filters
                  </Button>
                </Col>

                <Col md={2} className='d-flex align-items-end'>
                  <Button
                    color='success'
                    onClick={handleExportExcel}
                    disabled={exporting || loading}
                    className='w-100'
                  >
                    {exporting ? (
                      <Spinner size='sm' className='me-1' />
                    ) : (
                      <i className='ri-file-excel-2-line align-middle me-1'></i>
                    )}
                    Export Excel
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Sales Table */}
          <Card>
            <CardBody>
              <div className='table-responsive'>
                <Table className='align-middle table-nowrap mb-0'>
                  <thead className='table-light'>
                    <tr>
                      <th>#</th>
                      <th>Booth Address</th>
                      <th>Photo Booth</th>
                      <th>Category</th>
                      <th>Background</th>
                      <th>Quantity</th>
                      <th>Amount</th>
                      <th>Payment Type</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={10} className='text-center py-4'>
                          <Spinner size='sm' className='me-2' /> Loading sales...
                        </td>
                      </tr>
                    )}

                    {!loading && sales.length === 0 && (
                      <tr>
                        <td colSpan={10} className='text-center py-4'>
                          <div className='text-muted'>
                            <i className='ri-inbox-line fs-2 d-block mb-2'></i>
                            No sales found. Try adjusting your filters.
                          </div>
                        </td>
                      </tr>
                    )}

                    {!loading &&
                      sales.map((sale, index) => {
                        const amount = sale.totalAmount ?? sale.price ?? 0
                        const currency = sale.currency || 'TRY'
                        const boothName = sale.photoBoothId?.name || '-'
                        const loc = sale.photoBoothId?.location
                        const boothLocation = loc
                          ? [loc.city, loc.address].filter(Boolean).join(', ') || '-'
                          : '-'
                        const categoryName =
                          getCategoryName(sale.orderId?.categoryId) !== '-'
                            ? getCategoryName(sale.orderId?.categoryId)
                            : sale.orderId?.activeActionId?.name ||
                              sale.orderId?.activeActionId?.label ||
                              '-'
                        const photoCount = sale.orderId?.photoCount || sale.products?.length || 0

                        return (
                          <tr key={sale._id} className='animate-fade-in'>
                            <td>{(currentPage - 1) * perPage + index + 1}</td>
                            <td>
                              <span className='fw-medium text-dark'>{boothLocation}</span>
                            </td>
                            <td>
                              <div className='d-flex align-items-center'>
                                <div className='avatar-xs me-2'>
                                  <span className='avatar-title rounded-circle bg-soft-primary text-primary'>
                                    <i className='ri-camera-line'></i>
                                  </span>
                                </div>
                                <span>{boothName}</span>
                              </div>
                            </td>
                            <td>{categoryName}</td>
                            {/* Arka plan: ADI yazılır, küçük görsel GÖSTERİLMEZ.
                                📏 2026-08-17 ÖLÇÜM: arka plan dosyaları ortalama
                                399 KB (165 dosya / 64,3 MB) ve burada 34×34 px'lik
                                bir kutuda gösteriliyorlardı — tarayıcı tam boy
                                JPEG'i indirip 34 piksele küçültüyordu. 100 satırlık
                                sayfada satır başına 2 arka plan ≈ 80 MB indirme.
                                Müşteri (2026-08-17): "Dashboard üzerinde sadece
                                arka plan adı yazması yeterli."
                                Küçük görsel geri istenirse önce gerçek bir
                                thumbnail üretimi (ör. sharp ile 64px) gerekir. */}
                            <td>
                              {sale.orderId?.selectedBackgroundNames &&
                              sale.orderId.selectedBackgroundNames.length > 0 ? (
                                <div className='d-flex flex-wrap gap-1'>
                                  {sale.orderId.selectedBackgroundNames.map((bgName, bi) => (
                                    <span
                                      key={bi}
                                      className='badge bg-light text-body border'
                                      title={bgName}
                                    >
                                      {bgName}
                                    </span>
                                  ))}
                                </div>
                              ) : sale.orderId?.selectedBackgrounds &&
                                sale.orderId.selectedBackgrounds.length > 0 ? (
                                // Ad çözülemedi (arka plan silinmiş olabilir) ama
                                // kullanıldığı kesin — "-" yazıp gizlemeyelim.
                                <span className='text-muted small'>
                                  {sale.orderId.selectedBackgrounds.length} arka plan (adı yok)
                                </span>
                              ) : noBackgroundReason(sale) ? (
                                // Etiket ve açıklama TEK yerden gelir
                                // (noBackgroundReason) — Excel sütunu da aynısını
                                // kullanır, ikisi ayrışamaz.
                                <span
                                  className='text-muted small'
                                  title={noBackgroundReason(sale)!.title}
                                >
                                  {noBackgroundReason(sale)!.label}
                                </span>
                              ) : (
                                <span className='text-muted'>-</span>
                              )}
                            </td>
                            <td>
                              <span className='badge bg-secondary text-white'>
                                {photoCount} photos
                              </span>
                            </td>
                            <td className='fw-semibold'>{formatCurrency(amount, currency)}</td>
                            <td>
                              <div className='d-flex flex-wrap gap-1'>
                                {/* promotionCode ödemelerinde "Cash" badge gösterilmez,
                                    sadece promo badge yeterli (aşağıda) */}
                                {sale.paymentType !== 'promotionCode' && (
                                  <span
                                    className={`badge ${sale.paymentType === 'cash' ? 'bg-info' : 'bg-primary'} text-white`}
                                  >
                                    {sale.paymentType === 'creditCard'
                                      ? 'Credit Card'
                                      : sale.paymentType === 'cash'
                                        ? 'Cash'
                                        : sale.paymentType || '-'}
                                  </span>
                                )}
                                {sale.orderId?.promoCode && (
                                  <span
                                    className={`badge ${
                                      sale.orderId?.promoDiscountType === 1
                                        ? 'bg-success text-white'
                                        : sale.orderId?.promoDiscountType === 2
                                          ? 'bg-secondary text-white'
                                          : 'bg-warning text-dark'
                                    }`}
                                    title={
                                      sale.orderId?.promoDiscountType === 1
                                        ? `%${sale.orderId?.promoDiscountValue || 0} indirim`
                                        : sale.orderId?.promoDiscountType === 2
                                          ? `${sale.orderId?.promoDiscountValue || 0} TL indirim`
                                          : 'Promosyon'
                                    }
                                  >
                                    <i
                                      className={`me-1 ${
                                        sale.orderId?.promoDiscountType === 1
                                          ? 'ri-percent-line'
                                          : sale.orderId?.promoDiscountType === 2
                                            ? 'ri-money-dollar-circle-line'
                                            : 'ri-coupon-line'
                                      }`}
                                    ></i>
                                    {sale.orderId.promoCode}
                                    {sale.orderId?.promoDiscountType === 1 &&
                                      sale.orderId?.promoDiscountValue && (
                                        <span className='ms-1'>
                                          (%{sale.orderId.promoDiscountValue})
                                        </span>
                                      )}
                                    {sale.orderId?.promoDiscountType === 2 &&
                                      sale.orderId?.promoDiscountValue && (
                                        <span className='ms-1'>
                                          ({sale.orderId.promoDiscountValue}₺)
                                        </span>
                                      )}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>{renderStatus(sale.status || sale.orderId?.status)}</td>
                            <td className='text-muted'>
                              {formatDate(sale.createdAt || sale.orderId?.createdAt)}
                            </td>
                          </tr>
                        )
                      })}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>

          {totalPages > 1 && (
            <Pagination
              perPageData={perPage}
              data={sales}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPage={totalPages}
            />
          )}
        </Container>
      </div>

      <style>{`
        .card-animate {
          transition: all 0.3s ease;
        }
        .card-animate:hover {
          transform: translateY(-5px);
          box-shadow: 0 0.75rem 1.5rem rgba(18, 38, 63, 0.1);
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </React.Fragment>
  )
}

export default Sales
